/**
 * Shared type definitions for audio recording and upload
 * 
 * @packageDocumentation
 */

/**
 * Audio recording platform
 */
export type AudioPlatform = 'web' | 'ios' | 'android';

/**
 * Supported audio formats
 */
export type AudioFormat = 'webm' | 'm4a' | 'mp4' | 'ogg' | 'wav';

/**
 * Audio recording status
 */
export type AudioRecordingStatus = 'uploading' | 'processing' | 'completed' | 'failed';

/**
 * Audio recording metadata
 */
export interface AudioMetadata {
  platform: AudioPlatform;
  format: AudioFormat;
  duration: number;
  sampleRate: number;
  recordedAt: string;
  deviceInfo?: DeviceInfo;
}

/**
 * Device information
 */
export interface DeviceInfo {
  device?: string;
  browser?: string;
  browserVersion?: string;
  os?: string;
  osVersion?: string;
}

/**
 * Audio recording model (API response)
 */
export interface AudioRecording {
  /** Recording unique identifier */
  id: string;
  /** User who created the recording */
  userId: string;
  /** Recording title (optional) */
  title?: string;
  /** Platform where recorded */
  platform: AudioPlatform;
  /** Audio file format */
  format: AudioFormat;
  /** Recording duration in seconds */
  duration: number;
  /** Audio sample rate in Hz */
  sampleRate: number;
  /** Current status */
  status: AudioRecordingStatus;
  /** URL to download audio file (signed, expires in 1 hour) */
  audioUrl?: string;
  /** URL to download transcript (if available) */
  transcriptUrl?: string;
  /** When recording was made */
  recordedAt: string;
  /** When record was created */
  createdAt: string;
  /** When record was last updated */
  updatedAt: string;
  /** When processing completed (if status=completed) */
  processedAt?: string;
  /** Error message (if status=failed) */
  error?: string;
}

/**
 * Paginated list of recordings
 */
export interface RecordingsList {
  recordings: AudioRecording[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Audio quality preset
 */
export type AudioQualityPreset = 'low' | 'medium' | 'high';

/**
 * Audio quality settings
 */
export interface AudioQualitySettings {
  /** Sample rate in Hz */
  sampleRate: number;
  /** Bitrate in bits per second */
  bitrate: number;
  /** Enable echo cancellation */
  echoCancellation: boolean;
  /** Enable noise suppression */
  noiseSuppression: boolean;
  /** Enable automatic gain control */
  autoGainControl: boolean;
}

/**
 * Get audio quality settings for a preset
 */
export function getQualitySettings(preset: AudioQualityPreset): AudioQualitySettings {
  switch (preset) {
    case 'low':
      return {
        sampleRate: 16000,
        bitrate: 64000,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
    case 'medium':
      return {
        sampleRate: 44100,
        bitrate: 96000,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
    case 'high':
      return {
        sampleRate: 48000,
        bitrate: 128000,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      };
  }
}

/**
 * Audio recorder state
 */
export type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped';

/**
 * Audio recorder configuration
 */
export interface AudioRecorderConfig {
  /** Audio quality preset */
  quality?: AudioQualityPreset;
  /** Custom quality settings (overrides preset) */
  customQuality?: AudioQualitySettings;
  /** Maximum recording duration in seconds (default: 7200 = 2 hours) */
  maxDuration?: number;
  /** Enable audio level monitoring */
  enableLevelMonitoring?: boolean;
  /** Audio format (web only) */
  format?: AudioFormat;
}

